import { useEffect, useMemo } from 'react';
import { Api } from '../application/panelApi';
import { ICON } from '../components/Icons';
import { confirmDialog, modal } from '../components/ModalHost';
import { toast } from '../components/ToastHost';
import { DEFAULT_NEURO_QUERIES } from '../domain/neuro';
import { scenarioLane, type ScenarioLane } from '../domain/scenarioLanes';
import { createFormStore, type FormStore } from '../lib/formStore';
import { errText, nf, plural } from '../lib/format';
import { useApp } from '../store/AppContext';

type Scenario = {
  id: string;
  name: string;
  engine?: string;
  lane?: string;
  depth: number;
  dwell: number;
  bounce: number;
  warmup?: boolean;
  back?: boolean;
  queries?: string[];
  farmDefault?: boolean;
};

type TaskRef = { scenario?: string };

type AuditFn = (act: string, obj?: string) => void;

function laneOf(x: Scenario): ScenarioLane {
  return scenarioLane(x.lane || x.engine || x.name);
}

function laneLabel(lane: ScenarioLane): string {
  if (lane === 'google') return 'Google';
  if (lane === 'common') return 'Общий';
  if (lane === 'neuro') return 'Copilot';
  return 'Bing';
}

function engineLabelForLane(lane: ScenarioLane): string {
  if (lane === 'google') return 'Google — Desktop';
  if (lane === 'common') return 'Общий';
  if (lane === 'neuro') return 'Copilot';
  return 'Bing — Desktop';
}



type ScenForm = {
  name: string;
  lane: ScenarioLane;
  depth: string;
  dwell: string;
  bounce: string;
  warm: boolean;
  back: boolean;
  delHours: string;
  queries: string;
  farmDef: boolean;
  busy: boolean;
};

function ScenBody({ store }: { store: FormStore<ScenForm> }) {
  const f = store.useValue();
  return (
    <>
      <div className="field-row">
        <label htmlFor="sName">Название</label>
        <input
          type="text"
          id="sName"
          placeholder="Например: Десктоп · базовый"
          value={f.name}
          onChange={(e) => store.set({ name: e.target.value })}
        />
      </div>
      <div className="field-row">
        <label htmlFor="sLane">Лента</label>
        <select
          id="sLane"
          value={f.lane}
          onChange={(e) => {
            const lane = scenarioLane(e.target.value);
            store.set({ lane, farmDef: lane === 'neuro' ? false : store.get().farmDef });
          }}
        >
          <option value="bing">Bing</option>
          {f.lane === 'common' ? <option value="common">Общий</option> : null}
          <option value="neuro">Нейро (Copilot)</option>
        </select>
      </div>
      {f.lane !== 'neuro' && (
        <>
          <div className="grid3">
            <div className="field-row">
              <label htmlFor="sDepth">Глубина просмотра</label>
              <input
                type="number"
                id="sDepth"
                min={1}
                max={20}
                value={f.depth}
                onChange={(e) => store.set({ depth: e.target.value })}
              />
            </div>
            <div className="field-row">
              <label htmlFor="sDwell">Время на странице, сек</label>
              <input
                type="number"
                id="sDwell"
                min={5}
                value={f.dwell}
                onChange={(e) => store.set({ dwell: e.target.value })}
              />
            </div>
            <div className="field-row">
              <label htmlFor="sBounce">Целевой отказ, %</label>
              <input
                type="number"
                id="sBounce"
                min={0}
                max={100}
                value={f.bounce}
                onChange={(e) => store.set({ bounce: e.target.value })}
              />
            </div>
          </div>
          <div className="set-row">
            <div className="txt">
              <b>Подготовка профиля перед визитом</b>
              <small>Перед основным запросом профиль сделает 2–3 посторонних поиска, чтобы визит выглядел естественно.</small>
            </div>
            <label className="switch">
              <input type="checkbox" id="sWarm" checked={f.warm} onChange={(e) => store.set({ warm: e.target.checked })} />
              <i></i>
            </label>
          </div>
          <div className="set-row">
            <div className="txt">
              <b>Возврат в выдачу</b>
              <small>После визита на целевой сайт профиль вернётся в выдачу и откроет соседние результаты.</small>
            </div>
            <label className="switch">
              <input type="checkbox" id="sBack" checked={f.back} onChange={(e) => store.set({ back: e.target.checked })} />
              <i></i>
            </label>
          </div>
          <div className="field-row">
            <label htmlFor="sDelHours">Удалять профиль через N ч после целевого клика (0 — не удалять)</label>
            <input
              type="number"
              id="sDelHours"
              min={0}
              max={8760}
              value={f.delHours}
              onChange={(e) => store.set({ delHours: e.target.value })}
            />
          </div>
        </>
      )}
      <div className="field-row">
        <label htmlFor="sQueries">{f.lane === 'neuro' ? 'Запросы в Copilot' : 'Запросы для подготовки профилей'}</label>
        <textarea
          id="sQueries"
          rows={8}
          spellCheck={false}
          placeholder="По одному запросу на строку, например: как выбрать кофемашину"
          value={f.queries}
          onChange={(e) => store.set({ queries: e.target.value })}
        />
      </div>
      {f.lane !== 'neuro' && (
        <div className="set-row">
          <div className="txt">
            <b>Сценарий подготовки профилей по умолчанию</b>
            <small>Если в ленте отмечен хотя бы один такой сценарий, подготовка профилей берёт запросы только из отмеченных.</small>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              id="sFarmDef"
              checked={f.farmDef}
              onChange={(e) => store.set({ farmDef: e.target.checked })}
            />
            <i></i>
          </label>
        </div>
      )}
    </>
  );
}

type ScenFootProps = {
  store: FormStore<ScenForm>;
  editing: Scenario | null;
  currentDelHours: number;
  close: () => void;
  audit: AuditFn;
  refreshScenarios: () => Promise<void>;
  refreshSettings: () => Promise<unknown>;
};

function ScenFoot({
  store,
  editing,
  currentDelHours,
  close,
  audit,
  refreshScenarios,
  refreshSettings,
}: ScenFootProps) {
  const f = store.useValue();
  const save = async () => {
    const cur = store.get();
    const name = cur.name.trim();
    if (!name) {
      toast('Введите название сценария — по нему вы будете выбирать его в задачах.', 'err');
      return;
    }
    const queries = cur.queries
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith('#'));
    const lane = scenarioLane(cur.lane);
    if (lane === 'neuro' && !queries.length) {
      toast('Добавьте хотя бы один вопрос для Copilot: по одному на строку, без # в начале.', 'err');
      return;
    }
    const data = {
      name,
      lane,
      engine: engineLabelForLane(lane),
      depth: +cur.depth || 4,
      dwell: +cur.dwell || 95,
      bounce: +cur.bounce || 12,
      warmup: cur.warm,
      back: cur.back,
      queries,
      farmDefault: lane === 'neuro' ? false : cur.farmDef,
    };
    const delHours = Math.max(0, +(cur.delHours || 0));
    store.set({ busy: true });
    try {
      if (editing) await Api.scenarios.update(editing.id, data);
      else await Api.scenarios.create(data);
      if (delHours !== currentDelHours) {
        await Api.settings.save({ settings: { deleteAfterTargetHours: delHours } });
        await refreshSettings().catch(() => undefined);
      }
      await refreshScenarios();
      close();
      audit(editing ? 'Изменил сценарий' : 'Создал сценарий', name);
      toast('Сценарий сохранён — его можно выбрать в задачах.', 'ok');
    } catch (err) {
      store.set({ busy: false });
      toast(errText(err), 'err');
    }
  };
  return (
    <>
      <button type="button" className="btn" data-close>
        Отмена
      </button>
      <button type="button" className="btn btn-primary" id="sSave" disabled={f.busy} onClick={() => void save()}>
        Сохранить
      </button>
    </>
  );
}



export function ScenariosPage() {
  const {
    scenarios: rawScenarios,
    tasks: rawTasks,
    ui,
    settingsPayload,
    refreshScenarios,
    refreshTasks,
    refreshSettings,
    audit,
  } = useApp();
  const scenarios = rawScenarios as Scenario[];
  const tasks = rawTasks as TaskRef[];
  const currentLane: 'bing' | 'google' = ui.searchEngine === 'google' ? 'google' : 'bing';

  useEffect(() => {
    void refreshScenarios().catch((err: unknown) => toast(errText(err), 'err'));
    void refreshTasks().catch(() => undefined);
  }, [refreshScenarios, refreshTasks]);

  const rows = useMemo(
    () =>
      scenarios.filter((s) => {
        const l = laneOf(s);
        return l === 'common' || l === currentLane || l === 'neuro';
      }),
    [scenarios, currentLane],
  );

  const deleteAfterHours = Number(settingsPayload?.settings?.deleteAfterTargetHours) || 0;

  const openEdit = (x: Scenario | null, laneOverride?: ScenarioLane) => {
    const lane = x ? laneOf(x) : (laneOverride ?? currentLane);
    const store = createFormStore<ScenForm>({
      name: x ? x.name : '',
      lane,
      depth: String(x ? x.depth : 4),
      dwell: String(x ? x.dwell : 95),
      bounce: String(x ? x.bounce : 12),
      warm: !x || Boolean(x.warmup),
      back: Boolean(x && x.back),
      delHours: String(deleteAfterHours),
      queries: (x?.queries ?? (lane === 'neuro' ? DEFAULT_NEURO_QUERIES : [])).join('\n'),
      farmDef: lane === 'neuro' ? false : !x || Boolean(x.farmDefault),
      busy: false,
    });
    const holder: { close: () => void } = { close: () => undefined };
    holder.close = modal(
      x ? 'Изменить сценарий' : 'Создать сценарий',
      <ScenBody store={store} />,
      <ScenFoot
        store={store}
        editing={x}
        currentDelHours={deleteAfterHours}
        close={() => holder.close()}
        audit={audit}
        refreshScenarios={refreshScenarios}
        refreshSettings={refreshSettings}
      />,
    );
  };

  const onCopy = async (x: Scenario) => {
    try {
      const lane = laneOf(x);
      const { id: _id, ...rest } = x;
      void _id;
      await Api.scenarios.create({
        ...rest,
        name: `${x.name} — копия`,
        lane,
        engine: engineLabelForLane(lane),
        farmDefault: false,
      });
      await refreshScenarios();
      audit('Дублировал сценарий', x.name);
      toast('Копия сценария создана — она появилась в списке с пометкой «копия».', 'ok');
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const onDelete = (x: Scenario) => {
    const used = tasks.filter((t) => t.scenario === x.name).length;
    if (used) {
      toast(`Сценарий нельзя удалить: его используют ${used} ${plural(used, 'задача', 'задачи', 'задач')}. Выберите в них другой сценарий и повторите удаление.`, 'err');
      return;
    }
    if (scenarios.length === 1) {
      toast('Нельзя удалить последний сценарий. Сначала создайте другой, затем удалите этот.', 'err');
      return;
    }
    confirmDialog('Удалить сценарий?', <>Сценарий «{x.name}» будет удалён насовсем. Отменить это действие нельзя.</>, async () => {
      try {
        await Api.scenarios.remove(x.id);
        await refreshScenarios();
        audit('Удалил сценарий', x.name);
        toast('Сценарий удалён.', 'err');
      } catch (err) {
        toast(errText(err), 'err');
      }
    });
  };

  return (
    <section id="page-scenarios">
      <div className="page-head">
        <div />
        <div className="actions">
          <button type="button" className="btn" id="newNeuroScenBtn" onClick={() => openEdit(null, 'neuro')}>
            Нейро-сценарий
          </button>
          <button type="button" className="btn btn-primary" id="newScenBtn" onClick={() => openEdit(null)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Создать сценарий
          </button>
        </div>
      </div>
      <div className="panel">
        <div className="panel-head">
          <h2>Библиотека сценариев</h2>
          <span className="count" id="scCount">
            {rows.length} {plural(rows.length, 'сценарий', 'сценария', 'сценариев')} ·{' '}
            {currentLane === 'google' ? 'Google' : 'Bing'} + общие + Copilot
          </span>
        </div>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Лента</th>
                <th style={{ textAlign: 'right' }}>Запросов</th>
                <th style={{ textAlign: 'right' }}>Глубина</th>
                <th style={{ textAlign: 'right' }}>Время, сек</th>
                <th style={{ textAlign: 'right' }}>Отказ, %</th>
                <th>Поведение</th>
                <th style={{ textAlign: 'right' }}>Задач</th>
                <th style={{ textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody id="scBody">
              {rows.length ? (
                rows.map((x) => {
                  const lane = laneOf(x);
                  const used = tasks.filter((t) => t.scenario === x.name).length;
                  const behavior =
                    lane === 'neuro'
                      ? [
                          <span key="n" className="tag">
                            Copilot
                          </span>,
                        ]
                      : [
                          x.warmup ? (
                            <span key="w" className="tag">
                              прогрев
                            </span>
                          ) : null,
                          x.back ? (
                            <span key="b" className="tag">
                              возврат в выдачу
                            </span>
                          ) : null,
                        ].filter(Boolean);
                  return (
                    <tr key={x.id} data-id={x.id}>
                      <td data-l="Название">
                        <b>{x.name}</b>
                        {x.farmDefault ? (
                          <>
                            {' '}
                            <span className="tag ok">прогрев</span>
                          </>
                        ) : null}
                        {lane === 'common' ? (
                          <>
                            {' '}
                            <span className="tag n">общий</span>
                          </>
                        ) : null}
                      </td>
                      <td data-l="Лента">
                        <span className="tag n">{laneLabel(lane)}</span>
                      </td>
                      <td data-l="Запросов" className="num">
                        {nf.format((x.queries || []).length)}
                      </td>
                      <td data-l="Глубина" className="num">
                        {x.depth}
                      </td>
                      <td data-l="Время" className="num">
                        {x.dwell}
                      </td>
                      <td data-l="Отказ" className="num">
                        {x.bounce}%
                      </td>
                      <td data-l="Поведение">
                        {behavior.length ? (
                          behavior.flatMap((el, i) => (i ? [' ', el] : [el]))
                        ) : (
                          <span className="tag n">базовое</span>
                        )}
                      </td>
                      <td data-l="Задач" className={`num ${used ? '' : 'dim'}`}>
                        {used}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="icon-btn" data-act="edit" title="Изменить настройки и запросы сценария." onClick={() => openEdit(x)}>
                            {ICON.edit}
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            data-act="copy"
                            title="Создать копию сценария, чтобы изменить её, не трогая оригинал."
                            onClick={() => void onCopy(x)}
                          >
                            {ICON.copy}
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            data-act="del"
                            title="Удалить сценарий. Если его используют задачи, сначала выберите в них другой."
                            onClick={() => onDelete(x)}
                          >
                            {ICON.del}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9}>
                    <div className="empty">Сценариев нет</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
